import { AddIcon, AlertIcon, Box, Flex, Typography } from '@iheartjane/reefer';

import {
  EventNames,
  ModalNames,
  SettingNames,
  track,
} from '@jane/business-admin/util';
import { SortableList } from '@jane/shared/components';
import type { SortableItem } from '@jane/shared/components';

export interface CarouselSlide {
  altText?: string;
  desktopId?: number;
  desktopImage?: string;
  hasError?: boolean;
  link?: string;
  mobileId?: number;
  mobileImage?: string;
  position: number;
  storeName?: string;
}

interface CarouselSlidesManagerProps {
  /** Note that slide position is 1-indexed, not 0-indexed */
  onSelectedSlideChange: (slidePosition: number) => void;
  onSlidesChange: (slides: CarouselSlide[]) => void;
  selectedSlidePosition: number;
  slides: CarouselSlide[];
  storeName?: string;
}

const SlideItem = ({
  slide,
  isSelected,
  onSelect,
}: {
  isSelected: boolean;
  onSelect: () => void;
  slide: CarouselSlide;
}) => {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      position="relative"
    >
      <Flex
        aria-label="Slide number"
        alignItems="center"
        justifyContent="center"
        width={48}
        height={48}
        border={isSelected ? 'primary' : 'grays-light'}
        borderWidth="2px"
        background="grays-white"
        onClick={onSelect}
        position="relative"
        style={{ borderRadius: '50%' }}
      >
        <Typography variant="body-semibold">{slide.position}</Typography>
        {slide.hasError && (
          <Box
            background="error"
            borderRadius="xl"
            position="absolute"
            top={-8}
            right={-8}
            p={0}
          >
            <AlertIcon
              altText={`Slide ${slide.position} has errors`}
              style={{ '--icon-height': '20px', '--icon-width': '20px' }}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export const CarouselSlidesManager = ({
  slides,
  selectedSlidePosition,
  onSlidesChange,
  onSelectedSlideChange,
}: CarouselSlidesManagerProps) => {
  const maxSlides = 5;
  const canAddMore = slides.length < maxSlides;

  const handleAddSlide = () => {
    if (!canAddMore) return;

    const newSlide: CarouselSlide = {
      position: slides.length + 1,
    };

    const newSlides = [...slides, newSlide];
    onSlidesChange(newSlides);
    onSelectedSlideChange(newSlide.position);

    track({
      event: EventNames.ModifiedSetting,
      setting_name: SettingNames.CarouselBanner,
      action: 'add_slide',
      modal_name: ModalNames.Carousel,
      revert: false,
    });
  };

  const handleReorderSlides = (
    items: SortableItem<CarouselSlide>[],
    _oldIndex: number,
    newIndex: number
  ) => {
    const reorderedSlides = items
      .map(({ item }) => item)
      .filter((item): item is CarouselSlide => item !== undefined)
      .map((slide, index) => ({ ...slide, position: index + 1 }));

    onSlidesChange(reorderedSlides);
    onSelectedSlideChange(newIndex + 1);

    track({
      event: EventNames.ModifiedSetting,
      setting_name: SettingNames.CarouselBanner,
      action: 'reorder',
      modal_name: ModalNames.Carousel,
      revert: false,
    });
  };

  const sortableSlides = slides.map((slide) => ({
    id: slide.position.toString(),
    item: slide,
  }));

  return (
    <Flex flexDirection="column" gap={16}>
      <Typography variant="body-semibold">Carousel slides</Typography>

      <Flex
        gap={16}
        alignItems="center"
        flexWrap="wrap"
        background="grays-white"
        p={16}
        borderRadius="sm"
      >
        <SortableList
          direction="horizontal"
          gap={16}
          items={sortableSlides}
          onChange={handleReorderSlides}
          renderItem={({ item: slide }) => {
            if (!slide) return null;
            return (
              <SlideItem
                slide={slide}
                isSelected={slide.position === selectedSlidePosition}
                onSelect={() => onSelectedSlideChange(slide.position)}
              />
            );
          }}
        />

        {canAddMore && (
          <Flex
            alignItems="center"
            alignSelf="flex-start"
            justifyContent="center"
            width={48}
            height={48}
            border="grays-light"
            borderWidth="2px"
            onClick={handleAddSlide}
            aria-label="Add new slide"
            style={{
              borderStyle: 'dashed',
              cursor: 'pointer',
              borderRadius: '50%',
            }}
          >
            <AddIcon color="default" />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
